// chartDataTransformers.js - Utility functions to transform Chart.js data format to Recharts format

/**
 * Transform Chart.js line/bar chart data to Recharts format
 * @param {Object} chartJsData - Chart.js data object with labels and datasets
 * @returns {Array} Recharts-compatible data array
 * 
 * Example input:
 * { labels: ['01/01', '01/05'], datasets: [{ label: 'Compliance', data: [85, 82] }] }
 * 
 * Example output:
 * [{ label: '01/01', compliance: 85 }, { label: '01/05', compliance: 82 }]
 */
export const transformLineChartData = (chartJsData) => {
    if (!chartJsData || !chartJsData.labels || !chartJsData.datasets) {
        return [];
    }

    const { labels, datasets } = chartJsData;

    return labels.map((label, index) => {
        const dataPoint = { label };

        datasets.forEach(dataset => {
            // Use lowercase dataset label as key for Recharts
            const key = dataset.label.toLowerCase().replace(/\s+/g, '_');
            dataPoint[key] = dataset.data[index];
        });

        return dataPoint;
    });
};

/**
 * Transform Chart.js bar chart data to Recharts format
 * Alias for transformLineChartData since they use the same structure
 */
export const transformBarChartData = transformLineChartData;

/**
 * Transform Chart.js pie/doughnut chart data to Recharts format
 * @param {Object} chartJsData - Chart.js data object with labels and datasets
 * @param {Array} colors - Optional array of colors (if not in datasets)
 * @returns {Array} Recharts-compatible pie data array
 * 
 * Example input:
 * { labels: ['Low', 'Medium', 'High'], datasets: [{ data: [65, 25, 10], backgroundColor: ['#2DCE89', ...] }] }
 * 
 * Example output:
 * [{ name: 'Low', value: 65, color: '#2DCE89' }, ...]
 */
export const transformPieChartData = (chartJsData, colors = null) => {
    if (!chartJsData || !chartJsData.labels || !chartJsData.datasets || !chartJsData.datasets[0]) {
        return [];
    }

    const { labels, datasets } = chartJsData;
    const values = datasets[0].data;
    const bgColors = colors || datasets[0].backgroundColor || [];

    return labels.map((label, index) => ({
        name: label,
        value: values[index],
        color: Array.isArray(bgColors) ? bgColors[index] : bgColors
    }));
};

/**
 * Transform stacked bar chart data (e.g., Changes By Date chart)
 * @param {Object} chartJsData - Chart.js data with multiple stacked datasets
 * @returns {Array} Recharts-compatible stacked bar data
 */
export const transformStackedBarData = (chartJsData) => {
    if (!chartJsData || !chartJsData.labels || !chartJsData.datasets) {
        return [];
    }

    const { labels, datasets } = chartJsData;

    return labels.map((label, index) => {
        const dataPoint = { label };

        datasets.forEach(dataset => {
            // Use lowercase dataset label as key
            const key = dataset.label.toLowerCase().replace(/\s+/g, '_');
            dataPoint[key] = dataset.data[index];
        });

        return dataPoint;
    });
};

/**
 * Transform stacked bar chart data explicitly for Device Details (which returns {t,y} object coordinates)
 * @param {Object} chartJsData - Chart.js data with multiple stacked datasets containing {t, y} values
 * @returns {Array} Recharts-compatible stacked bar data with primitives
 */
export const transformDeviceDetailsStackedBarData = (chartJsData) => {
    if (!chartJsData || !chartJsData.labels || !chartJsData.datasets) {
        return [];
    }

    const { labels, datasets } = chartJsData;

    return labels.map((label, index) => {
        const dataPoint = { label };

        datasets.forEach(dataset => {
            // Use lowercase dataset label as key
            const key = dataset.label.toLowerCase().replace(/\s+/g, '_');
            
            // Handle {t, y} object arrays seamlessly for Recharts
            const val = dataset.data[index];
            dataPoint[key] = (val && typeof val === 'object' && 'y' in val) ? val.y : val;
        });

        return dataPoint;
    });
};

/**
 * Extract dataset configurations for Recharts (colors, names, etc.)
 * @param {Object} chartJsData - Chart.js data object
 * @returns {Array} Array of dataset configurations
 */
export const extractDatasetConfigs = (chartJsData) => {
    if (!chartJsData || !chartJsData.datasets) {
        return [];
    }

    return chartJsData.datasets.map(dataset => ({
        key: dataset.label.toLowerCase().replace(/\s+/g, '_'),
        name: dataset.label,
        color: dataset.backgroundColor || dataset.borderColor || '#000'
    }));
};
